/**
 * Copyright © 2025 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, h } from 'gridjs';
import 'gridjs/dist/theme/mermaid.css';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import api from '../api';
import { toast } from 'react-toastify';

const PAGE_SIZE = 20;

// `message` stays in the filter shape and the API still supports it, but there is no UI control for
// it: a dropdown would have to hardcode the message list from SovosCoupa/Helper/Constants.cs and would
// silently go stale as soon as a new message is added. Azure Table Storage has no substring matching,
// so a free-text message search needs a different approach before the control comes back.
const EMPTY_FILTERS = { invoiceId: '', message: '', fromDate: '', toDate: '' };

// Scroll-loading stops here and waits for a click. The biggest customer has ~50,000 log rows, so
// uncapped scrolling would let the browser accumulate the whole history one screen at a time.
const AUTO_LOAD_ROW_LIMIT = 200;

function LogsPage() {
    const [logs, setLogs] = useState([]);
    const [continuationToken, setContinuationToken] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [autoLoad, setAutoLoad] = useState(true);
    const [loadError, setLoadError] = useState(false);
    // Scroll-loading runs until this many rows, then waits for a click. Each click raises it by
    // another AUTO_LOAD_ROW_LIMIT, so scrolling resumes for the next batch instead of the page
    // becoming click-only once the first cap is reached.
    const [autoLoadCeiling, setAutoLoadCeiling] = useState(AUTO_LOAD_ROW_LIMIT);

    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const [showModal, setShowModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const gridRef = useRef(null);
    const gridWrapperRef = useRef(null);
    const sentinelRef = useRef(null);

    const queryParams = new URLSearchParams(window.location.search);
    const customer = {
        username: queryParams.get('user'),
        storeName: queryParams.get('store'),
    };

    const authHeaders = () => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
    });

    // The server returns one page at a time, newest first, plus a continuation token. There is no
    // total count and no jump-to-page: Azure Table Storage cannot count rows without scanning them.
    const fetchPage = useCallback(async (token, activeFilters) => {
        const body = {
            username: customer.username,
            storeName: customer.storeName,
            pageSize: PAGE_SIZE,
        };
        if (token) body.continuationToken = token;
        if (activeFilters.invoiceId) body.invoiceId = activeFilters.invoiceId.trim();
        if (activeFilters.message) body.message = activeFilters.message;
        if (activeFilters.fromDate) body.fromDate = `${activeFilters.fromDate}T00:00:00Z`;
        if (activeFilters.toDate) body.toDate = `${activeFilters.toDate}T23:59:59Z`;

        const response = await api.post('/api/Log/paged', body, { headers: authHeaders() });
        return response.data;
    }, [customer.username, customer.storeName]);

    const loadFirstPage = useCallback(async (activeFilters) => {
        // A new filter is a new result set: re-arm scroll loading and go back to the top, otherwise
        // the user stays scrolled into the middle of a list that no longer exists.
        setAutoLoad(true);
        setLoadError(false);
        setAutoLoadCeiling(AUTO_LOAD_ROW_LIMIT);
        try {
            const data = await fetchPage(null, activeFilters);
            setLogs(data.items || []);
            setContinuationToken(data.continuationToken || null);
            setHasMore(!!data.hasMore);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error('Failed to fetch logs:', error);
            setLogs([]);
            setContinuationToken(null);
            setHasMore(false);
            setLoadError(true);
        }
    }, [fetchPage]);

    const loadMore = useCallback(async (auto = false) => {
        if (!continuationToken || loadingMore) return;
        setLoadingMore(true);
        setLoadError(false);
        try {
            const data = await fetchPage(continuationToken, filters);
            setLogs(prev => [...prev, ...(data.items || [])]);
            setContinuationToken(data.continuationToken || null);
            setHasMore(!!data.hasMore);
        } catch (error) {
            console.error('Failed to load more logs:', error);
            // Stop auto-loading on failure, otherwise the observer would retry in a tight loop
            // against an endpoint that is already failing. The user retries deliberately instead.
            setLoadError(true);
            setAutoLoad(false);
        } finally {
            setLoadingMore(false);
        }
        // auto is only used to distinguish the caller in future telemetry; behaviour is identical
        void auto;
    }, [continuationToken, loadingMore, fetchPage, filters]);

    useEffect(() => {
        loadFirstPage(EMPTY_FILTERS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = (e) => {
        e.preventDefault();
        loadFirstPage(filters);
    };

    const resetFilters = () => {
        setFilters(EMPTY_FILTERS);
        loadFirstPage(EMPTY_FILTERS);
    };

    // Auto-load the next page when the sentinel below the grid scrolls into view.
    //
    // Capped deliberately: the largest customer has ~50,000 log rows, and unbounded scroll-loading
    // would let someone pull all of them into the browser - which is the problem this page was
    // rebuilt to avoid. After AUTO_LOAD_ROW_LIMIT rows the user continues by clicking.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        if (!hasMore || !autoLoad || loadError) return;
        if (logs.length >= autoLoadCeiling) return;

        const observer = new IntersectionObserver(entries => {
            if (entries[0]?.isIntersecting) loadMore(true);
        }, { rootMargin: '200px' });

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, autoLoad, loadError, logs.length, autoLoadCeiling, loadMore]);

    // Clicking through the cap lifts it by another batch, so scrolling takes over again.
    const continueLoading = () => {
        setAutoLoadCeiling(prev => Math.max(prev, logs.length) + AUTO_LOAD_ROW_LIMIT);
        setAutoLoad(true);
        setLoadError(false);
        loadMore(false);
    };

    const autoLoadPaused = hasMore && !loadError && logs.length >= autoLoadCeiling;

    // The payload is deliberately absent from the list response - it holds whole tax XML documents and
    // is what made the old page slow. Fetch it only when a row is opened.
    const openLog = useCallback(async (log) => {
        setSelectedLog({ ...log, customData: null });
        setShowModal(true);
        setDetailLoading(true);
        try {
            const response = await api.get('/api/Log/detail', {
                params: {
                    invoiceId: log.invoiceId,
                    logId: log.logId,
                    username: customer.username,
                    storeName: customer.storeName,
                },
                headers: authHeaders(),
            });
            setSelectedLog(prev => ({ ...prev, ...response.data }));
        } catch (error) {
            console.error('Failed to fetch log detail:', error);
            setSelectedLog(prev => ({ ...prev, customData: '' }));
        } finally {
            setDetailLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer.username, customer.storeName]);

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedLog(null);
    };

    // Memoised: this is an effect dependency, and rebuilding the array on every render would
    // re-run forceRender() on every render rather than only when the rows actually change.
    const gridRows = useMemo(
        () => logs.map(log => [log.invoiceId, log.timestamp, log.message, log.logId]),
        [logs]
    );

    useEffect(() => {
        if (!gridWrapperRef.current) return;

        // Update in place rather than destroy/recreate. Recreating removes the table from the DOM,
        // which collapses the page height and makes the browser clamp the scroll position - so every
        // scroll-triggered append would jump the user and re-trigger the observer. Updating keeps the
        // node, so the list only ever grows downwards and the scroll position holds.
        if (gridRef.current) {
            gridRef.current.updateConfig({ data: gridRows }).forceRender();
            return;
        }

        const grid = new Grid({
            columns: [
                { id: 'invoiceId', name: 'Invoice Id' },
                {
                    id: 'timestamp',
                    name: 'Timestamp',
                    formatter: (cell) => (cell ? new Date(cell).toLocaleString() : ''),
                },
                { id: 'message', name: 'Message' },
                {
                    id: 'actions',
                    name: 'Logs',
                    sort: false,
                    width: '90px',
                    formatter: (_, row) => h('button', {
                        className: 'btn btn-primary btn-sm',
                        title: 'View log',
                        onclick: () => openLog({
                            invoiceId: String(row.cells[0].data ?? ''),
                            timestamp: row.cells[1].data,
                            message: String(row.cells[2].data ?? ''),
                            logId: String(row.cells[3].data ?? ''),
                        }),
                    }, h('i', { className: 'fa-solid fa-eye' })),
                },
                { id: 'logId', name: 'logId', hidden: true },
            ],
            data: gridRows,
            // Paging, sorting and searching are all done by the server. Leaving Grid.js to do them
            // client-side would only ever act on the rows currently loaded.
            pagination: false,
            search: false,
            sort: false,
            language: { noRecordsFound: 'No logs found for this filter.' },
        });

        grid.render(gridWrapperRef.current);
        gridRef.current = grid;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridRows, openLog]);

    // Tear the grid down only when the page itself unmounts.
    useEffect(() => () => {
        if (gridRef.current) {
            gridRef.current.destroy();
            gridRef.current = null;
        }
    }, []);

    const tryParseJson = (text) => {
        try {
            return { ok: true, value: JSON.parse(text) };
        } catch (e) {
            return { ok: false };
        }
    };

    // The API stores every payload via JSON serialization, so a string payload (the XML sent to
    // Coupa, or a raw Sovos response body) arrives as a JSON string literal: quoted, with its
    // newlines escaped. Re-stringifying that would show the quotes and literal "\n", so unwrap it
    // and show the text as-is - or pretty-print it if the string itself holds JSON.
    const formatPayload = (raw) => {
        if (!raw) return '';
        const parsed = tryParseJson(raw);
        if (!parsed.ok) return raw;
        if (typeof parsed.value !== 'string') return JSON.stringify(parsed.value, null, 2);

        const inner = tryParseJson(parsed.value);
        return inner.ok && inner.value !== null && typeof inner.value === 'object'
            ? JSON.stringify(inner.value, null, 2)
            : parsed.value;
    };

    const copyPayload = () => {
        const text = formatPayload(selectedLog?.customData);
        navigator.clipboard.writeText(text)
            .then(() => toast.success('Copied to clipboard!'))
            .catch(err => console.error('Failed to copy: ', err));
    };

    return (
        <div className="container">
            <h2>Logs - {customer.username}</h2>
            <hr />

            <Form onSubmit={applyFilters} className="mb-3">
                <Row className="g-2 align-items-end">
                    <Col md={4}>
                        <Form.Label className="mb-1 small">Invoice Id</Form.Label>
                        <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Exact invoice id"
                            value={filters.invoiceId}
                            onChange={e => setFilters({ ...filters, invoiceId: e.target.value })}
                        />
                    </Col>
                    {/* Message filter is hidden for now. The API still accepts a `message` value, but a
                        dropdown would have to hardcode the message list, and it silently goes stale as
                        soon as a new one is added in Constants.cs. Re-add the control here when we have
                        a way to search message text rather than pick from a fixed list. */}
                    <Col md={3}>
                        <Form.Label className="mb-1 small">From</Form.Label>
                        <Form.Control
                            size="sm"
                            type="date"
                            value={filters.fromDate}
                            onChange={e => setFilters({ ...filters, fromDate: e.target.value })}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label className="mb-1 small">To</Form.Label>
                        <Form.Control
                            size="sm"
                            type="date"
                            value={filters.toDate}
                            onChange={e => setFilters({ ...filters, toDate: e.target.value })}
                        />
                    </Col>
                    <Col md={2} className="d-flex gap-2">
                        <Button size="sm" type="submit" variant="primary">Search</Button>
                        <Button size="sm" type="button" variant="secondary" onClick={resetFilters}>Reset</Button>
                    </Col>
                </Row>
            </Form>

            <div ref={gridWrapperRef}></div>

            {/* Sentinel: when this scrolls into view the next page loads automatically. */}
            <div ref={sentinelRef} />

            <div className="d-flex flex-column align-items-center my-3 gap-2">
                <span className="text-muted small">
                    Showing {logs.length} log{logs.length === 1 ? '' : 's'}
                    {!hasMore && logs.length > 0 && ' — end of results'}
                </span>

                {loadingMore && (
                    <span className="text-muted small d-flex align-items-center gap-2">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Loading more...
                    </span>
                )}

                {loadError && (
                    <div className="d-flex flex-column align-items-center gap-1">
                        <span className="text-danger small">Could not load more logs.</span>
                        <Button variant="outline-danger" size="sm" onClick={continueLoading} disabled={loadingMore}>
                            Retry
                        </Button>
                    </div>
                )}

                {/* Past the cap the user opts in to each further batch, so a long scroll can never
                    pull an entire customer's history into the browser. */}
                {autoLoadPaused && !loadingMore && (
                    <Button variant="outline-primary" size="sm" onClick={continueLoading}>
                        Load {PAGE_SIZE} more
                    </Button>
                )}

                {/* Fallback if the browser has no IntersectionObserver, or auto-load was switched off. */}
                {hasMore && !autoLoadPaused && !loadError && !loadingMore && !autoLoad && (
                    <Button variant="outline-primary" size="sm" onClick={continueLoading}>
                        Load {PAGE_SIZE} more
                    </Button>
                )}
            </div>

            <Modal show={showModal} onHide={handleCloseModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Log Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <div>
                            <p><strong>Invoice Id:</strong> {selectedLog.invoiceId || 'N/A'}</p>
                            <p><strong>Timestamp:</strong> {selectedLog.timestamp ? new Date(selectedLog.timestamp).toLocaleString() : 'N/A'}</p>
                            <p><strong>Message:</strong> {selectedLog.message || 'N/A'}</p>
                            <p><strong>Logs Data:</strong></p>
                            <div
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px',
                                    padding: '1rem',
                                    position: 'relative',
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {!detailLoading && (
                                    <button
                                        className="btn btn-sm btn-light"
                                        style={{ position: 'absolute', top: '10px', right: '10px', border: '1px solid #ddd' }}
                                        title="Copy to clipboard"
                                        onClick={copyPayload}
                                    >
                                        <i className="fa fa-copy"></i> Copy
                                    </button>
                                )}
                                <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', marginTop: '2rem', marginBottom: 0 }}>
                                    {detailLoading ? 'Loading...' : formatPayload(selectedLog.customData)}
                                </pre>
                            </div>
                            {selectedLog.stackTrace ? (
                                <>
                                    <p className="mt-3"><strong>Stack Trace:</strong></p>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: '6px', padding: '1rem', maxHeight: '250px', overflowY: 'auto' }}>
                                        {selectedLog.stackTrace}
                                    </pre>
                                </>
                            ) : null}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default LogsPage;
